import React, { useState, useEffect, useRef } from "react";

export const useViewport = () => {
  const [width, setWidth] = React.useState(window.innerWidth);
  const breakpoint = 768;

  React.useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  return { width, breakpoint };
};

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [delay, value]);

  return debouncedValue;
}

export function useFirstMountState(): boolean {
  const isFirstLoad = useRef(true);

  // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  if (isFirstLoad.current) {
    // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    isFirstLoad.current = false;
    return true;
  }

  // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  return isFirstLoad.current;
}

// useful to rerender the components when any of the deps change, but only after the first load of the component
// note that the dependencies passed into the useEffect call is intentionally skipping the isFirstMount and effect references:
// those would result in the effect() being invoked again on a subsequent rerender even if deps didn't change which is not the desired behaviour
export const useUpdateEffect: typeof useEffect = (effect, deps) => {
  const isFirstMount = useFirstMountState();
  useEffect(() => {
    if (!isFirstMount) {
      effect();
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};
